/** 績效圖表與出缺席統計渲染。自 public/js/charts.js 移植。
 *
 *  四張績效圖原本是寫死的靜態示意,但它們以正式頁面呈現給學生看,畫面上沒有
 *  任何「示意資料」標示 —— 看到的人會以為那是自己的成績。現已改由
 *  GET /api/student/stats/performance 的真實資料驅動。
 *
 *  沒有資料時各圖自行顯示空狀態,而不是畫出一張看起來像真的空圖。
 */
import { escapeHtml } from './markdown';
import type { AttendanceRow, PerformanceStats } from './types';

const PURPLE = '#6C61E1';

function empty(host: HTMLElement, text: string): void {
  host.innerHTML = '<div style="padding:28px;color:#8C8C8C;font-size:14px;text-align:center">'
    + `${escapeHtml(text)}</div>`;
}

// ── 雷達圖(各課程類別的平均成績)──────────────────────────
function renderRadar(data: PerformanceStats['radar']): void {
  const host = document.getElementById('perf-radar');
  if (!host) return;
  if (data.length < 3) {
    empty(host, data.length ? '需要三個以上的課程類別才畫得出雷達圖' : '尚無已評分的成績');
    return;
  }

  const cx = 150;
  const cy = 145;
  const R = 100;
  const n = data.length;
  // 軸位置改為依類別數量計算(原本寫死 5 軸與 5 組標籤座標)
  const pt = (i: number, r: number): [number, number] => {
    const a = ((-90 + (360 / n) * i) * Math.PI) / 180;
    return [cx + r * Math.cos(a), cy + r * Math.sin(a)];
  };

  let s = '<svg viewBox="0 0 300 290" style="width:300px;height:290px">';
  for (const k of [0.25, 0.5, 0.75, 1]) {
    const pts = Array.from({ length: n }, (_, i) => {
      const [x, y] = pt(i, R * k);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    });
    s += `<polygon points="${pts.join(' ')}" fill="none" stroke="#E4E4EC" stroke-width="1"/>`;
  }
  for (let i = 0; i < n; i += 1) {
    const [x, y] = pt(i, R);
    s += `<line x1="${cx}" y1="${cy}" x2="${x.toFixed(1)}" y2="${y.toFixed(1)}" `
      + 'stroke="#E4E4EC" stroke-width="1"/>';
  }

  const dp = data.map((d, j) => {
    const [x, y] = pt(j, (R * Math.max(0, Math.min(100, d.value))) / 100);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });
  s += `<polygon points="${dp.join(' ')}" fill="rgba(108,97,225,0.32)" `
    + `stroke="${PURPLE}" stroke-width="2"/>`;

  for (const v of [20, 40, 60, 80, 100]) {
    const [x, y] = pt(0, (R * v) / 100);
    s += `<text x="${(x + 5).toFixed(1)}" y="${(y + 4).toFixed(1)}" `
      + `font-size="10" fill="#B5B5B5">${v}</text>`;
  }

  data.forEach((d, k) => {
    const [x, y] = pt(k, R + 22);
    const anchor = Math.abs(x - cx) < 8 ? 'middle' : (x > cx ? 'start' : 'end');
    s += `<text x="${x.toFixed(1)}" y="${(y + 4).toFixed(1)}" font-size="13" fill="#333" `
      + `text-anchor="${anchor}">${escapeHtml(d.label)} ${d.value}</text>`;
  });

  host.innerHTML = `${s}</svg>`;
}

// ── 每門課的作業 / 測驗 / 出席(三項各自 0-100)───────────────
function renderBars(data: PerformanceStats['bars']): void {
  const host = document.getElementById('perf-bars');
  if (!host) return;
  if (!data.length) {
    empty(host, '本學期尚無修課紀錄');
    return;
  }
  // 原本畫成堆疊比例,但這三項是各自獨立的 0-100 指標,堆疊起來的寬度沒有意義。
  // 改為三條各自對齊 0-100 的橫條。
  const series: [string, 'hw' | 'quiz' | 'attendance', string][] = [
    ['作業', 'hw', '#9DAEF0'], ['測驗', 'quiz', '#A4DEC9'], ['出席', 'attendance', '#F2C383'],
  ];
  let h = '';
  for (const row of data) {
    h += '<div style="margin-bottom:14px">'
      + `<div style="font-size:15px;color:#555;margin-bottom:6px">${escapeHtml(row.name)}</div>`;
    for (const [label, key, col] of series) {
      const v = Math.max(0, Math.min(100, row[key] ?? 0));
      h += '<div style="display:flex;align-items:center;gap:8px;margin-bottom:4px">'
        + `<span style="width:34px;font-size:12px;color:#8C8C8C;flex:none">${label}</span>`
        + '<div style="flex:1;height:18px;background:#F2F2F6;border-radius:5px;overflow:hidden">'
        + `<div style="width:${v}%;height:100%;background:${col}"></div></div>`
        + `<span style="width:30px;font-size:12px;color:#555;text-align:right">${v}</span>`
        + '</div>';
    }
    h += '</div>';
  }
  host.innerHTML = h;
}

// ── 熱點圖(學習活動的星期 × 時段)──────────────────────────
function renderHeat(data: PerformanceStats['heat']): void {
  const host = document.getElementById('perf-heat');
  if (!host) return;
  const counts = new Map(data.map((d) => [`${d.period}-${d.dow}`, d.n]));
  const max = data.reduce((m, d) => Math.max(m, d.n), 0);
  if (!max) {
    empty(host, '尚無學習活動紀錄');
    return;
  }
  // 0 = 無活動(CSS 的淺色),其餘依相對強度分四級
  const level = (n: number): number => (n <= 0 ? 0 : Math.max(1, Math.ceil((n / max) * 4)));

  const rows: [string, number][] = [['早上', 0], ['下午', 1], ['晚上', 2]];
  const dow = ['一', '二', '三', '四', '五', '六', '日'];
  let h = '<div class="perf__heatmap-grid">';
  h += `<div style="font-size:13px;color:#8C8C8C">時段 ${String.fromCharCode(92)} 星期</div>`;
  for (const d of dow) {
    h += `<div style="text-align:center;font-size:15px;color:#555">${d}</div>`;
  }
  for (const [label, period] of rows) {
    h += `<div style="font-size:15px;color:#555">${label}</div>`;
    for (let i = 1; i <= 7; i += 1) {
      const n = counts.get(`${period}-${i}`) ?? 0;
      h += `<div class="perf__heatmap-cell perf__heatmap-cell--${level(n)}" `
        + `title="${dow[i - 1]} ${label}:${n} 次"></div>`;
    }
  }
  host.innerHTML = `${h}</div>`;
}

// ── 折線圖(成績走勢,依週彙總)──────────────────────────────
function renderLine(data: PerformanceStats['line']): void {
  const host = document.getElementById('perf-line');
  if (!host) return;
  if (data.length < 2) {
    empty(host, data.length ? '至少要兩週的成績才畫得出走勢' : '尚無已評分的成績');
    return;
  }

  const W = 560;
  const H = 240;
  const padL = 40;
  const padB = 30;
  const padT = 10;
  const padR = 14;
  const plotW = W - padL - padR;
  const plotH = H - padT - padB;
  const n = data.length;

  const X = (i: number) => padL + (plotW * i) / (n - 1);
  const Y = (v: number) => padT + plotH * (1 - Math.max(0, Math.min(100, v)) / 100);

  let sv = `<svg viewBox="0 0 ${W} ${H}" style="width:100%;height:240px">`;
  for (const v of [0, 20, 40, 60, 80, 100]) {
    const y = Y(v).toFixed(1);
    sv += `<line x1="${padL}" y1="${y}" x2="${W - padR}" y2="${y}" stroke="#EDEDED"/>`
      + `<text x="${padL - 6}" y="${parseFloat(y) + 4}" font-size="11" `
      + `fill="#9A9A9A" text-anchor="end">${v}</text>`;
  }

  let d = '';
  let dots = '';
  data.forEach((p, i) => {
    const x = X(i).toFixed(1);
    const y = Y(p.value).toFixed(1);
    d += `${i ? ' L ' : 'M '}${x} ${y}`;
    dots += `<circle cx="${x}" cy="${y}" r="3.5" fill="${PURPLE}">`
      + `<title>${escapeHtml(p.week)}:${p.value} 分</title></circle>`;
  });
  sv += `<path d="${d}" fill="none" stroke="${PURPLE}" stroke-width="2.5"/>${dots}`;

  // 週數多時只標頭尾與中間,避免標籤擠在一起
  const ticks = n <= 6 ? data.map((_, i) => i) : [0, Math.floor((n - 1) / 2), n - 1];
  for (const i of ticks) {
    const p = data[i];
    if (!p) continue;
    sv += `<text x="${X(i).toFixed(1)}" y="${H - 8}" font-size="12" fill="#555" `
      + `text-anchor="middle">${escapeHtml(p.week)}</text>`;
  }
  host.innerHTML = `${sv}</svg>`;
}

// ── 出缺席長條圖(真實 API 資料;超過 3 門課可左右捲動)──────
export function renderAttendance(data: AttendanceRow[]): void {
  const host = document.getElementById('attendance-chart');
  if (!host) return;

  // 上:曠課 中:請假 下:出席
  const bars = (data || []).map((x) => ({
    label: x.name,
    vals: [x.absent, x.leave, x.present] as [number, number, number],
  }));
  if (!bars.length) {
    host.innerHTML = '<div style="color:#8C8C8C;font-size:14px">尚無出缺席資料</div>';
    return;
  }

  const cols = ['#E8A0A0', '#9DAEF0', '#A4DEC9'];
  const maxH = 216;
  const rawMax = Math.max(...bars.map((b) => b.vals[0] + b.vals[1] + b.vals[2]));
  const maxVal = Math.max(5, Math.ceil(rawMax / 5) * 5);
  const yLabels: number[] = [];
  for (let yi = 5; yi >= 0; yi -= 1) yLabels.push(Math.round((maxVal * yi) / 5));
  const barSlot = 90; // 每門課佔寬,>3 門時內容寬超出可捲動
  const innerW = Math.max(bars.length * barSlot, 1);

  let h = '<div style="background:#F4F2FD;border-radius:14px;padding:20px 18px 14px">'
    + '<div style="position:relative;height:260px;padding-left:36px">'
    + '<div style="position:absolute;left:0;top:0;bottom:24px;width:30px;display:flex;'
    + 'flex-direction:column;justify-content:space-between;font-size:13px;color:#9A9A9A;text-align:right">'
    + yLabels.map((v) => `<span>${v}</span>`).join('')
    + '</div>'
    + '<div style="position:absolute;left:36px;right:0;top:0;bottom:0;overflow-x:auto">'
    + `<div style="position:relative;min-width:100%;width:${innerW}px;height:236px">`
    + `<div style="position:absolute;left:0;right:0;top:0;height:${maxH}px">`;

  for (const p of [0, 20, 40, 60, 80, 100]) {
    h += `<div style="position:absolute;left:0;right:0;top:${p}%;border-top:1px solid #E6E3F5"></div>`;
  }

  h += '<div style="position:absolute;inset:0;display:flex;justify-content:space-around;'
    + 'align-items:flex-end">';
  for (const b of bars) {
    h += '<div style="width:62px;display:flex;flex-direction:column;justify-content:flex-end">';
    b.vals.forEach((v, k) => {
      const pxH = Math.round((v / maxVal) * maxH);
      let radius = '';
      if (k === 0) radius = 'border-radius:6px 6px 0 0';
      else if (k === 2) radius = 'border-radius:0 0 6px 6px';
      h += `<div style="height:${pxH}px;background:${cols[k]};${radius}" title="${v}"></div>`;
    });
    h += '</div>';
  }
  h += '</div></div>';

  h += '<div style="position:absolute;left:0;right:0;bottom:0;height:22px;display:flex;'
    + 'justify-content:space-around;align-items:center;font-size:14px;color:#555">';
  for (const b of bars) {
    h += `<span style="width:${barSlot - 8}px;text-align:center;white-space:nowrap;`
      + `overflow:hidden;text-overflow:ellipsis">${b.label}</span>`;
  }
  h += '</div></div></div></div>';

  const legend = (color: string, label: string) =>
    '<span style="display:flex;align-items:center;gap:6px">'
    + `<span style="width:10px;height:10px;border-radius:50%;background:${color};`
    + `display:inline-block"></span>${label}</span>`;
  h += '<div style="display:flex;justify-content:center;gap:24px;font-size:15px;'
    + 'color:#494949;margin-top:6px">'
    + legend('#A4DEC9', '正常出席')
    + legend('#9DAEF0', '請假')
    + legend('#E8A0A0', '曠課')
    + '</div>';

  host.innerHTML = h;
}

/** 我的績效頁的四張圖(資料來自 GET /api/student/stats/performance)。 */
export function renderAll(stats: PerformanceStats): void {
  renderRadar(stats.radar ?? []);
  renderBars(stats.bars ?? []);
  renderHeat(stats.heat ?? []);
  renderLine(stats.line ?? []);
}
